import { useRef } from 'react'
import toast from 'react-hot-toast'
import { Transforms, Range, Editor, Element as SlateElement } from 'slate'

const MAX = {
  width: 400,
  height: 600,
  bytes: 4.5 * 1024 * 1024
}

const ALLOWED = ['image/jpeg', 'image/png', 'image/webp']

export function useInsertImage (editor) {
  const fileInputRef = useRef(null)

  const openPicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!ALLOWED.includes(file.type)) {
      toast.error('Unsupported image type')
      return
    }

    if (file.size > MAX.bytes) {
      toast.error('File exceeds 4.5 MB')
      return
    }

    try {
      const dataUrl = await readFileAsDataURL(file)
      const resized = await resizeDataUrl(dataUrl)

      Transforms.insertNodes(editor, {
        type: 'image',
        url: resized,
        children: [{ text: '' }]
      })
    } catch (err) {
      toast.error('Failed to load image')
    }

    e.target.value = ''
  }

  const input = (
    <input
      ref={fileInputRef}
      type='file'
      accept='image/*'
      hidden
      onChange={handleFile}
    />
  )

  return { openPicker, input }
}

export const insertLink = (editor, url) => {
  if (!url) return

  const { selection } = editor
  const isCollapsed = selection && Range.isCollapsed(selection)

  const link = {
    type: 'link',
    url,
    children: isCollapsed ? [{ text: url }] : []
  }

  if (isCollapsed) {
    Transforms.insertNodes(editor, link)
  } else {
    Transforms.unwrapNodes(editor, {
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n.type === 'link',
    })

    Transforms.wrapNodes(editor, link, { split: true })
    Transforms.collapse(editor, { edge: 'end' })
  }
}
function readFileAsDataURL (file) {
  return new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && typeof FileReader !== 'undefined') {
      const reader = new window.FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(file)
    }
  })
}

function resizeDataUrl (src) {
  return new Promise((resolve, reject) => {
    if (typeof window !== 'undefined') {
      const img = new window.Image()
      img.onload = () => {
        const { width, height } = getScaled(img, MAX)
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        resolve(canvas.toDataURL('image/png'))
      }
      img.onerror = reject
      img.src = src
    }
  })
}

function getScaled (img, { width: maxWidth, height: maxHeight }) {
  let width = img.width
  let height = img.height

  if (width > maxWidth || height > maxHeight) {
    if (width > height) {
      height = (height * maxWidth) / width
      width = maxWidth
    } else {
      width = (width * maxHeight) / height
      height = maxHeight
    }
  }

  return { width, height }
}
